import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  error: Error | null;
};

/**
 * Catches render-time errors in the subtree and shows a full-page fallback
 * instead of an unhandled exception (white screen).
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[AppErrorBoundary]", error, errorInfo.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    const { children } = this.props;

    if (error) {
      const isDev = import.meta.env.DEV;

      return (
        <div className="min-h-dvh bg-background">
          <Header />
          <main className="container py-12">
            <div className="mx-auto max-w-lg flex flex-col items-center gap-6 text-center">
              <AlertCircle className="size-12 text-destructive shrink-0" aria-hidden />
              <div className="space-y-2">
                <h1 className="page-title">Что-то пошло не так</h1>
                <p className="text-muted-foreground text-sm">
                  Страница не смогла отобразиться. Попробуйте обновить или перейти в другой раздел.
                </p>
              </div>

              <Alert variant="destructive" className="text-left w-full">
                <AlertTitle>Ошибка интерфейса</AlertTitle>
                <AlertDescription>
                  {isDev ? (
                    <span className="font-mono text-xs break-words">{error.message}</span>
                  ) : (
                    "Произошла непредвиденная ошибка. Если она повторяется, сообщите менеджеру."
                  )}
                </AlertDescription>
              </Alert>

              {isDev && error.stack ? (
                <details className="w-full text-left rounded-md border border-border bg-muted/40 p-3 text-xs">
                  <summary className="cursor-pointer font-medium text-foreground">
                    Стек вызова (только в dev)
                  </summary>
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-muted-foreground">
                    {error.stack}
                  </pre>
                </details>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:justify-center">
                <Button type="button" variant="default" onClick={() => window.location.reload()}>
                  Обновить страницу
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link to="/account">На главную</Link>
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link to="/catalog">В каталог</Link>
                </Button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return children;
  }
}
